import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Button } from '../../shared/components/button/button';
import { DeleteBatchModal } from '../../shared/components/delete-batch-modal/delete-batch-modal';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { GitHubConnector, GitHubRepository, GitHubConnectionSummary } from '../../core/services/github-connector';
import { CodeGraph } from '../../core/services/code-graph';

@Component({
  selector: 'app-repositories',
  standalone: true,
  imports: [CommonModule, Sidebar, Navbar, Button, ToastComponent, DeleteBatchModal],
  templateUrl: './repositories.html',
  styleUrl: './repositories.css'
})
export class Repositories implements OnInit {
  private githubService = inject(GitHubConnector);
  private codeGraphService = inject(CodeGraph);
  private toast = inject(Toast);
  private router = inject(Router);

  connections = signal<GitHubConnectionSummary[]>([]);
  selectedConnectionId = signal<string | null>(null);
  isLoadingConnections = signal(true);

  repositories = signal<GitHubRepository[]>([]);
  isLoading = signal(true);
  trackedRepoNames = signal<Set<string>>(new Set());
  ingestingRepos = signal<Set<string>>(new Set());
  uningestingRepos = signal<Set<string>>(new Set());
  pendingUningest = signal<string | null>(null);
  cameFromClassifiedDocuments = signal(false);

  // Repos confirmed (via the code-graph endpoint's node count) to have an actual
  // graph. `isTracked` alone isn't enough — a repo can be selected/ingested and
  // still end up with 0 entities (e.g. an unsupported language), so we check the
  // real node count rather than trusting the tracked-repos list.
  reposWithGraph = signal<Set<string>>(new Set());
  checkingGraphStatus = signal<Set<string>>(new Set());

  ngOnInit() {
    this.cameFromClassifiedDocuments.set(!!history.state?.fromClassifiedDocuments);
    this.loadConnections();
  }

  goBack() {
    if (this.cameFromClassifiedDocuments()) {
      this.router.navigate(['/documents/classified']);
    } else {
      this.router.navigate(['/data-sources']);
    }
  }

  loadConnections() {
    this.isLoadingConnections.set(true);
    this.githubService.getConnections().subscribe({
      next: (res) => {
        this.isLoadingConnections.set(false);
        const connections = res.data.connections || [];
        this.connections.set(connections);
        if (connections.length > 0) {
          this.selectConnection(connections[0].id);
        } else {
          this.isLoading.set(false);
        }
      },
      error: (err) => {
        this.isLoadingConnections.set(false);
        this.isLoading.set(false);
        const message = err?.error?.message || 'Failed to load GitHub connections.';
        this.toast.show(message, 'error');
      }
    });
  }

  onConnectionChange(connectionId: string) {
    this.selectConnection(connectionId);
  }

  private selectConnection(connectionId: string) {
    this.selectedConnectionId.set(connectionId);
    this.repositories.set([]);
    this.trackedRepoNames.set(new Set());
    this.reposWithGraph.set(new Set());
    this.loadRepositories();
    this.loadTracked();
  }

  loadRepositories() {
    const connectionId = this.selectedConnectionId();
    if (!connectionId) return;
    this.isLoading.set(true);
    this.githubService.getRepositories(connectionId).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.repositories.set(res.data.repositories || []);
      },
      error: (err) => {
        this.isLoading.set(false);
        const message = err?.error?.message || 'Failed to load repositories from GitHub.';
        this.toast.show(message, 'error');
      }
    });
  }

  loadTracked() {
    const connectionId = this.selectedConnectionId();
    if (!connectionId) return;
    this.codeGraphService.getSelectedRepos(connectionId).subscribe({
      next: (res) => {
        this.trackedRepoNames.set(new Set(res.repoNames || []));
        this.checkGraphStatus(Array.from(this.trackedRepoNames()));
      },
      error: () => {}
    });
  }

  // Checks real node counts for the given repos so we don't rely on `isTracked`
  // (selected-for-ingestion) as a stand-in for "actually has a graph".
  checkGraphStatus(repoNames: string[]) {
    const connectionId = this.selectedConnectionId();
    if (repoNames.length === 0 || !connectionId) return;

    this.checkingGraphStatus.update(current => {
      const next = new Set(current);
      repoNames.forEach(name => next.add(name));
      return next;
    });

    forkJoin(
      repoNames.map(name =>
        this.codeGraphService.getGraph(connectionId, name, 0, 1, 1).pipe(
          map(res => ({ name, hasGraph: (res.meta?.nodeCount ?? 0) > 0 })),
          catchError(() => of({ name, hasGraph: false }))
        )
      )
    ).subscribe(results => {
      this.checkingGraphStatus.update(current => {
        const next = new Set(current);
        repoNames.forEach(name => next.delete(name));
        return next;
      });
      this.reposWithGraph.update(current => {
        const next = new Set(current);
        results.forEach(r => r.hasGraph ? next.add(r.name) : next.delete(r.name));
        return next;
      });
    });
  }

  isIngesting(repoName: string): boolean {
    return this.ingestingRepos().has(repoName);
  }

  isUningesting(repoName: string): boolean {
    return this.uningestingRepos().has(repoName);
  }

  isTracked(repoName: string): boolean {
    return this.trackedRepoNames().has(repoName);
  }

  hasGraph(repoName: string): boolean {
    return this.reposWithGraph().has(repoName);
  }

  isCheckingGraph(repoName: string): boolean {
    return this.checkingGraphStatus().has(repoName);
  }

  ingestRepo(repoName: string) {
    const connectionId = this.selectedConnectionId();
    if (this.isIngesting(repoName) || this.isUningesting(repoName) || !connectionId) return;

    const updated = new Set(this.trackedRepoNames());
    updated.add(repoName);
    this.trackedRepoNames.set(updated);

    const ingesting = new Set(this.ingestingRepos());
    ingesting.add(repoName);
    this.ingestingRepos.set(ingesting);

    this.codeGraphService.setSelectedRepos(connectionId, Array.from(updated)).subscribe({
      next: () => this.runIngestion(connectionId, repoName),
      error: (err) => {
        const done = new Set(this.ingestingRepos());
        done.delete(repoName);
        this.ingestingRepos.set(done);
        const message = err?.error?.message || 'Failed to save repository selection.';
        this.toast.show(message, 'error');
      }
    });
  }

  // Re-triggers ingestion for a repo that's already tracked but has no graph
  // (a previous ingestion attempt failed or produced 0 entities) — no need to
  // re-save the selection since it's already tracked.
  retryIngest(repoName: string) {
    const connectionId = this.selectedConnectionId();
    if (this.isIngesting(repoName) || this.isUningesting(repoName) || !connectionId) return;

    const ingesting = new Set(this.ingestingRepos());
    ingesting.add(repoName);
    this.ingestingRepos.set(ingesting);

    this.runIngestion(connectionId, repoName);
  }

  private runIngestion(connectionId: string, repoName: string) {
    this.codeGraphService.triggerIngestion(connectionId, repoName).subscribe({
      next: (res) => {
        const done = new Set(this.ingestingRepos());
        done.delete(repoName);
        this.ingestingRepos.set(done);
        const failed = res.results.filter(r => !r.success).length;
        if (failed > 0) {
          this.toast.show(`Ingestion of ${repoName} failed. Check logs for details.`, 'error');
        } else {
          this.toast.show(`${repoName} ingested successfully.`, 'success');
        }
        this.checkGraphStatus([repoName]);
      },
      error: (err) => {
        const done = new Set(this.ingestingRepos());
        done.delete(repoName);
        this.ingestingRepos.set(done);
        const message = err?.error?.message || 'Ingestion failed to start.';
        this.toast.show(message, 'error');
        this.checkGraphStatus([repoName]);
      }
    });
  }

  uningestRepo(repoName: string) {
    const connectionId = this.selectedConnectionId();
    if (!connectionId || this.isIngesting(repoName) || this.isUningesting(repoName)) return;
    this.pendingUningest.set(repoName);
  }

  cancelUningest() {
    const repoName = this.pendingUningest();
    if (repoName && this.isUningesting(repoName)) return;
    this.pendingUningest.set(null);
  }

  confirmUningest() {
    const repoName = this.pendingUningest();
    const connectionId = this.selectedConnectionId();
    if (!repoName || !connectionId || this.isUningesting(repoName)) return;

    const pending = new Set(this.uningestingRepos());
    pending.add(repoName);
    this.uningestingRepos.set(pending);

    this.codeGraphService.uningest(connectionId, repoName).subscribe({
      next: () => {
        const done = new Set(this.uningestingRepos());
        done.delete(repoName);
        this.uningestingRepos.set(done);
        this.pendingUningest.set(null);

        const tracked = new Set(this.trackedRepoNames());
        tracked.delete(repoName);
        this.trackedRepoNames.set(tracked);

        const graphs = new Set(this.reposWithGraph());
        graphs.delete(repoName);
        this.reposWithGraph.set(graphs);

        this.toast.show(`${repoName} un-ingested.`, 'success');
      },
      error: (err) => {
        const done = new Set(this.uningestingRepos());
        done.delete(repoName);
        this.uningestingRepos.set(done);
        this.pendingUningest.set(null);
        const message = err?.error?.message || 'Un-ingest failed.';
        this.toast.show(message, 'error');
      }
    });
  }

  onViewGraph(repoName: string) {
    const connectionId = this.selectedConnectionId();
    this.router.navigate(['/data-sources/repositories/graph'], { queryParams: { repo: repoName, connectionId } });
  }

  onViewCompleteGraph() {
    const connectionId = this.selectedConnectionId();
    this.router.navigate(['/data-sources/repositories/graph'], { queryParams: { connectionId } });
  }
}

